
/**
 * Module dependencies.
 */

import { CollectionProps, MediaProps } from 'src/types/collection';
import { mediasCollectionName } from 'src/core/collections-config/media';
import { units } from 'src/styles/utils';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import Loading from 'src/components/core/loading';
import React, { ReactElement, useMemo } from 'react';
import Type from 'src/components/core/typography/index';
import filter from 'lodash/filter';
import get from 'lodash/get';
import head from 'lodash/head';
import keys from 'lodash/keys';
import map from 'lodash/map';
import split from 'lodash/split';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  collections: CollectionProps[],
  filterName: string,
  isLoading: boolean,
  isSuccess: boolean,
  type: 'all' | 'new'
};

/**
 * `LoadingWrapper` styled component.
 */

const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 150px;
`;

/**
 * Get image.
 *
 */

function getImage(media: MediaProps[]): string | null | undefined {
  const imageCloseup = filter(media, ({ collectionName }) => {
    return collectionName === mediasCollectionName.imageCloseup;
  });

  return get(head(imageCloseup), 'fileUrl');
}

/**
 * `CollectionFilter` component.
 */

const CollectionFilter = (props: Props): ReactElement => {
  const { translate } = useTranslate();
  const { collections, filterName, isLoading, isSuccess, type } = props;
  const hasCollections = collections.length > 0;
  const { filters } = useFiltersList();
  const selectedFilters = useMemo(() => {
    const hasFilter = keys(filters).includes(filterName);

    return hasFilter ? split(filters[filterName], ',').map((id: string) => {
      return get(head(filter(collections, { id })), 'name');
    }) : [];
  }, [collections, filterName, filters]);

  return (
    <FilterDisclosure
      name={type}
      selectedFilters={selectedFilters}
      title={translate(`collections:list.filters.collections.${type}.title`)}
    >
      {isLoading && (
        <LoadingWrapper>
          <Loading active />
        </LoadingWrapper>
      )}

      {isSuccess && !hasCollections && (
        <Type.Paragraph paddingVertical={units(1)}>
          {translate(`collections:list.filters.collections.${type}.empty`)}
        </Type.Paragraph>
      )}

      {isSuccess && hasCollections && map(collections, ({ id, media, name }: CollectionProps) => (
        <FilterItem
          key={id}
          label={(
            <FilterItemLabel
              title={name}
              type={'image'}
              value={getImage(media)}
            />
          )}
          name={filterName}
          value={id}
        />
      ))}
    </FilterDisclosure>
  );
};

/**
 * Export `CollectionFilter` component.
 */

export default CollectionFilter;
